import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Gauge, LogOut, Settings, Receipt, Video } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '#/components/ui/avatar'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/components/ui/dropdown-menu'
import { authClient } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'

export function UserMenu() {
  const { data, isPending } = authClient.useSession()
  const navigate = useNavigate()
  const { data: sub } = useQuery<{ planType: string }>({
    queryKey: ['subscription', 'status'],
    queryFn: () => fetch('/api/subscription/status').then((r) => r.json()),
    enabled: Boolean(data?.user),
    staleTime: 60_000,
  })
  if (isPending) return <div className="size-8 animate-pulse rounded-full bg-muted" />
  if (!data?.user) {
    return (
      <Button asChild size="sm" className="h-8">
        <Link to="/sign-in">{m.common_signin()}</Link>
      </Button>
    )
  }
  const u = data.user
  const planName = sub?.planType ? sub.planType.replace(/-\d+mo$/, '') : 'free'
  // Design Room plan F4: only Facilitator-tier accounts get the shortcut (base slug strips -usd/-Nmo, see basePlanSlug).
  const isFacilitator = sub?.planType?.replace(/-usd$/, '').replace(/-\d+mo$/, '') === 'facilitator'
  return (
    <div className="flex items-center gap-2">
      <Badge variant="secondary" className="hidden capitalize sm:inline-flex">
        {planName === 'free' ? m.header_plan_free() : planName}
      </Badge>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="rounded-full outline-none ring-ring focus-visible:ring-2" aria-label={u.name}>
            <Avatar className="size-8">
              <AvatarImage src={u.image ?? undefined} alt="" />
              <AvatarFallback>{(u.name || u.email).charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="truncate">
            <div className="text-sm font-medium">{u.name}</div>
            <div className="truncate text-xs font-normal text-muted-foreground">{u.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/settings">
              <Settings className="size-4" /> {m.settings_title()}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/transactions" search={{ order: undefined }}>
              <Receipt className="size-4" /> {m.tx_title()}
            </Link>
          </DropdownMenuItem>
          {isFacilitator ? (
            <DropdownMenuItem asChild>
              <Link to="/facilitator">
                <Video className="size-4" /> {m.fac_menu_item()}
              </Link>
            </DropdownMenuItem>
          ) : null}
          {(u as { role?: string }).role === 'admin' ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/admin/dashboard">
                  <Gauge className="size-4" /> {m.usermenu_admin()}
                </Link>
              </DropdownMenuItem>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={async () => {
              await authClient.signOut()
              navigate({ to: '/' })
            }}
          >
            <LogOut className="size-4" /> {m.common_signout()}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
